import React, { useMemo } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/singleAccordionRowStyle';
import { makeStyles } from '@material-ui/core';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { Caption, BodyM, BodyMBold, BodyS, BodySBold } from 'components/Typography/Typography';
import Button from 'components/CustomButtons/Button';
import Badge from 'components/Badge/Badge';
import classNames from 'classnames';
import CustomTooltip from 'components/CustomTooltip/CustomTooltip';
import { translate } from 'components/Translation';
import moment from 'utils/date/moment';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IExtendedSingleButton extends types.services.ISingleButton {
	onClick: () => void;
}

interface ISingleAccordionRowProps {
	id: string;
	name: string;
	buttons: IExtendedSingleButton[];
	status?: types.services.IStatus;
	description?: string;
	tooltip?: types.services.IServicesTooltip;
	numberOfButtons: number;
}

const SingleAccordionRow = ({
	id,
	name,
	buttons,
	status,
	description = '',
	tooltip,
	numberOfButtons,
}: ISingleAccordionRowProps) => {
	const classes = useStyles({ numberOfButtons });

	const badgeDefaultColor = useMemo(() => {
		if (status) {
			switch (status.type) {
				case 'ACTIVE':
					return 'success';
				case 'INACTIVE':
					return 'gray';
				case 'PENDING':
				case 'PENDING_ACTIVATION':
					return 'warning';
				case 'PENDING_TERMINATION':
					return 'danger';
			}
		}
		return 'gray';
	}, [status]);

	const badgeColor = status?.color || badgeDefaultColor;

	const badgeDefaultText = useMemo(() => {
		if (status) {
			switch (status.type) {
				case 'ACTIVE':
					return translate('badge.active');
				case 'INACTIVE':
					return translate('badge.inactive');
				case 'PENDING':
					return translate('badge.pending');
				case 'PENDING_ACTIVATION':
					return translate('badge.PENDING_ACTIVATION');
				case 'PENDING_TERMINATION':
					return translate('badge.PENDING_TERMINATION');
			}
		}
		return '';
	}, [status]);

	const badgeText = status?.text || badgeDefaultText;

	return (
		<div id={`package-${id}`} className={classes.singleAccordionRowWrapper}>
			<div className={classes.singleAccordionRowNameAndTooltipWrapper}>
				<ConditionalRender
					show={status?.type === 'ACTIVE'}
					onTrue={() => (
						<BodyMBold color="primary-light" disableMargin>
							{name}
						</BodyMBold>
					)}
					onFalse={() => (
						<BodyM color="primary-light" disableMargin>
							{name}
						</BodyM>
					)}
				/>
				<ConditionalRender
					show={!!tooltip}
					onTrue={() => (
						<div className={classNames(classes.tooltip, 'package-tooltip')}>
							<CustomTooltip placement="bottom" className={tooltip?.classes}>
								{tooltip?.content || ''}
							</CustomTooltip>
						</div>
					)}
				/>
			</div>
			<div className={classes.singleAccordionRowStatusAndButtonsWrapper}>
				<div className={classes.singleAccordionRowStatusAndDateWrapper}>
					<ConditionalRender
						show={!!status && !status.hidden}
						onTrue={() => (
							<>
								<ConditionalRender
									show={!!status?.date}
									onTrue={() => (
										<div className={classes.singleAccordionRowDateWrapper}>
											<BodyS color="secondary-light" disableMargin>
												{translate('status.order.placed.on')}
											</BodyS>
											<BodySBold color="secondary-light" disableMargin>
												{moment(status?.date).format('YYYY-MM-DD')}
											</BodySBold>
										</div>
									)}
								/>
								<Badge color={badgeColor} className={classes.singleAccordionRowBadge}>
									<BodyS color="secondary-light" disableMargin>
										{badgeText}
									</BodyS>
								</Badge>
							</>
						)}
					/>
				</div>
				<div className={classes.singleAccordionRowButtonsWrapper}>
					{buttons.map((singleButton, index) => (
						<Button
							key={index}
							color={singleButton.color}
							disabled={singleButton.disabled}
							onClick={singleButton.onClick}
							className={classes.singleAccordionButton}
						>
							<>
								<ConditionalRender show={!!singleButton.icon} onTrue={() => singleButton.icon} />
								{singleButton.text}
							</>
						</Button>
					))}
				</div>
			</div>
			<ConditionalRender
				show={!!description}
				onTrue={() => (
					<div className={classes.singleAccordionRowDescriptionWrapper}>
						<Caption color="secondary-light" disableMargin>
							{description}
						</Caption>
					</div>
				)}
			/>
		</div>
	);
};

export default SingleAccordionRow;
